import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { trackFormErrors } from '@acorn-portal/shared/src/utils/tracking/track-form-errors';
import { useFormikContext, type FormikContextType } from 'formik';
import { useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Formik doesn't have a way to log errors when the form is submitted.
 * It's possible to use Formik's state to log the errors when the form is submitted.
 * The state goes through various steps when the form is submitted.
 * We need to track the steps and log the errors after the validation is complete.
 * It's possible to have multiple steps within the same render so if-else isn't used
 */
export const useFormikLogErrors = <T extends FormikContextType<unknown>>(f: T): void => {
	const { pathname } = useLocation();
	const ref = useRef({ submitCount: 0, step: 0 });
	const { track } = useTrack();
	const trackData = trackFormErrors<T>(pathname);

	if (!f) {
		throw new Error('Formik context is required');
	}

	if (f.submitCount < ref.current.submitCount) {
		ref.current.submitCount = f.submitCount;
		ref.current.step = 0;
	}

	if (ref.current.step === 0 && f.submitCount > ref.current.submitCount) {
		ref.current.step = 1;
		ref.current.submitCount = f.submitCount;
	}

	if (ref.current.step === 1 && f.isSubmitting && f.isValidating) {
		ref.current.step = 2;
	}

	if (ref.current.step === 2 && !f.isSubmitting && !f.isValidating) {
		ref.current.step = 3;
	}

	if (ref.current.step === 3 && Object.keys(f.errors || {}).length > 0) {
		const data = trackData(f.errors);
		track('form-validation-errors', data);
		ref.current.step = 0;
	}

	if (ref.current.step === 3 && Object.keys(f.errors || {}).length === 0) {
		ref.current.step = 0;
	}
};

export const FormikLogErrors = (): null => {
	const f = useFormikContext();
	useFormikLogErrors(f);
	return null;
};
